/**
 * ResidentBoost Chatbot Block
 *
 * Injection block that loads the ResidentBoost AI chatbot widget.
 * Per-property: each property has a unique apartment_id.
 * Skips injection when no apartment_id is available.
 */

import { useEffect, useRef } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';

const SCRIPT_ID = 'residentboost-chatbot-script';

function ResidentBoostChatbotComponent(_props: BlockProps) {
  const apartmentId = useBlockToken('apartment_id', {
    defaultField: 'custom_residentboost_apartment_id',
  });
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!apartmentId) return;

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://apis-v2.residentboost.com/chatbot/script?apartmentId=${apartmentId}`;
    script.async = true;
    document.head.appendChild(script);

    return () => {
      mountedRef.current = false;
      const el = document.getElementById(SCRIPT_ID);
      if (el) el.remove();
    };
  }, [apartmentId]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'ResidentBoostChatbot',
  displayName: 'ResidentBoost Chatbot',
  tokens: {
    apartment_id: {
      label: 'Apartment ID',
      type: 'text',
      defaultField: 'custom_residentboost_apartment_id',
      suggestedFields: ['custom_residentboost_apartment_id'],
      description: 'ResidentBoost apartment identifier (per-property).',
      required: true,
    },
  },
};

export const residentboostChatbotBlock: BlockDefinition = {
  id: 'residentboost-chatbot',
  name: 'ResidentBoost Chatbot',
  description: 'AI leasing chatbot powered by ResidentBoost. Automatically connects via property apartment ID.',
  category: 'Integrations',
  placement: 'injection',
  defaultScope: 'template',
  component: ResidentBoostChatbotComponent,
  contentSchema,
  tags: ['chatbot', 'residentboost', 'ai', 'leasing', 'lead'],
};
